import ApiService from './api.service.js';
import logger from '@/utils/logger';

class SalesReportService {
    constructor() {
        this.apiService = ApiService;
    }

    /**
     * Extract invoice rows from API list response shapes.
     */
    extractInvoicesFromResponse(response) {
        if (!response?.data) {
            return [];
        }
        if (Array.isArray(response.data)) {
            return response.data;
        }
        if (Array.isArray(response.data.data)) {
            return response.data.data;
        }
        if (Array.isArray(response.data.rows)) {
            return response.data.rows;
        }
        return [];
    }

    /**
     * Fetch company invoices including line items (required for item-level reports).
     */
    async fetchCompanyInvoicesWithItems(companyId) {
        const invoices = [];
        let page = 1;
        let hasMore = true;

        while (hasMore) {
            const url = `/customer/invoices?companyId=${companyId}&includeItems=true&limit=1000&page=${page}`;
            const response = await this.apiService.get(url);
            const batch = this.extractInvoicesFromResponse(response);
            invoices.push(...batch);

            const pagination = response?.data?.pagination;
            if (pagination && typeof pagination.hasMore === 'boolean') {
                hasMore = pagination.hasMore;
            } else {
                hasMore = batch.length === 1000;
            }
            page += 1;
        }

        return invoices;
    }

    /**
     * Get Sales by Customer Summary
     * Aggregates invoice totals by customer for a given date range
     */
    async getSalesByCustomerSummary(companyId, params = {}) {
        try {
            const queryParams = new URLSearchParams();

            if (params.fromDate) {
                queryParams.append('fromDate', params.fromDate);
            }
            if (params.toDate) {
                queryParams.append('toDate', params.toDate);
            }
            if (params.reportBasis) {
                queryParams.append('reportBasis', params.reportBasis);
            }

            // Fetch all invoices for the company
            const url = `/customer/invoices?companyId=${companyId}`;
            const response = await this.apiService.get(url);

            // Extract invoices from response
            let invoices = [];
            if (response.data) {
                if (Array.isArray(response.data)) {
                    invoices = response.data;
                } else if (response.data.data && Array.isArray(response.data.data)) {
                    invoices = response.data.data;
                } else if (response.data.rows && Array.isArray(response.data.rows)) {
                    invoices = response.data.rows;
                }
            }

            // Filter invoices by date range if provided
            if (params.fromDate || params.toDate) {
                invoices = invoices.filter(invoice => {
                    if (!invoice.date) return false;
                    const invoiceDate = invoice.date.split('T')[0]; // Handle both date and datetime

                    if (params.fromDate && invoiceDate < params.fromDate) {
                        return false;
                    }
                    if (params.toDate && invoiceDate > params.toDate) {
                        return false;
                    }
                    return true;
                });
            }

            // Filter invoices based on report basis and status
            // For accrual basis: include all non-draft, non-cancelled invoices
            // For cash basis: only include paid invoices
            const reportBasis = params.reportBasis || 'accrual';
            let filteredInvoices = invoices.filter(invoice => {
                // Exclude draft and cancelled invoices
                if (invoice.status === 'draft' || invoice.status === 'cancelled') {
                    return false;
                }

                // Filter by customer if specified
                if (params.customerId) {
                    const invoiceCustomerId = typeof invoice.customerId === 'string' 
                        ? parseInt(invoice.customerId) 
                        : invoice.customerId;
                    const filterCustomerId = typeof params.customerId === 'string' 
                        ? parseInt(params.customerId) 
                        : params.customerId;
                    if (invoiceCustomerId !== filterCustomerId) {
                        return false;
                    }
                }

                // For cash basis, only include paid invoices
                if (reportBasis === 'cash') {
                    return invoice.status === 'paid' || invoice.status === 'partially_paid';
                }

                return true;
            });

            // Aggregate by customer
            const customerSalesMap = new Map();

            filteredInvoices.forEach(invoice => {
                const customerId = invoice.customerId;
                const customerName = invoice.customer?.customerName ||
                    invoice.customerName ||
                    `Customer #${customerId}`;
                const totalAmount = parseFloat(invoice.totalAmount || 0);

                if (customerSalesMap.has(customerId)) {
                    const existing = customerSalesMap.get(customerId);
                    existing.amount += totalAmount;
                } else {
                    customerSalesMap.set(customerId, {
                        customerId: customerId,
                        customerName: customerName,
                        amount: totalAmount
                    });
                }
            });

            // Convert map to array and sort by customer name
            const summaryData = Array.from(customerSalesMap.values())
                .sort((a, b) => a.customerName.localeCompare(b.customerName));

            return {
                success: true,
                data: summaryData,
                metadata: {
                    totalCustomers: summaryData.length,
                    totalSales: summaryData.reduce((sum, item) => sum + item.amount, 0),
                    fromDate: params.fromDate,
                    toDate: params.toDate,
                    reportBasis: reportBasis
                }
            };
        } catch (error) {
            logger.error('Error fetching sales by customer summary:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Error fetching sales by customer summary',
                error: error
            };
        }
    }

    /**
     * Get Sales by Customer Detail
     * Returns detailed invoice list grouped by customer
     * For cash basis, returns receive payment records instead of invoices
     */
    async getSalesByCustomerDetail(companyId, params = {}) {
        try {
            const reportBasis = params.reportBasis || 'accrual';

            // For cash basis, fetch receive payments instead of invoices
            if (reportBasis === 'cash') {
                return await this.getSalesByCustomerDetailCash(companyId, params);
            }

            // For accrual basis, use existing invoice logic
            const queryParams = new URLSearchParams();

            if (params.fromDate) {
                queryParams.append('fromDate', params.fromDate);
            }
            if (params.toDate) {
                queryParams.append('toDate', params.toDate);
            }
            if (params.reportBasis) {
                queryParams.append('reportBasis', params.reportBasis);
            }

            // Fetch all invoices for the company
            const url = `/customer/invoices?companyId=${companyId}`;
            const response = await this.apiService.get(url);

            // Extract invoices from response
            let invoices = [];
            if (response.data) {
                if (Array.isArray(response.data)) {
                    invoices = response.data;
                } else if (response.data.data && Array.isArray(response.data.data)) {
                    invoices = response.data.data;
                } else if (response.data.rows && Array.isArray(response.data.rows)) {
                    invoices = response.data.rows;
                }
            }

            // Filter invoices by date range if provided
            if (params.fromDate || params.toDate) {
                invoices = invoices.filter(invoice => {
                    if (!invoice.date) return false;
                    const invoiceDate = invoice.date.split('T')[0]; // Handle both date and datetime

                    if (params.fromDate && invoiceDate < params.fromDate) {
                        return false;
                    }
                    if (params.toDate && invoiceDate > params.toDate) {
                        return false;
                    }
                    return true;
                });
            }

            // Filter invoices based on report basis and status
            let filteredInvoices = invoices.filter(invoice => {
                if (invoice.status === 'draft' || invoice.status === 'cancelled') {
                    return false;
                }

                // Filter by customer if specified
                if (params.customerId) {
                    const invoiceCustomerId = typeof invoice.customerId === 'string' 
                        ? parseInt(invoice.customerId) 
                        : invoice.customerId;
                    const filterCustomerId = typeof params.customerId === 'string' 
                        ? parseInt(params.customerId) 
                        : params.customerId;
                    if (invoiceCustomerId !== filterCustomerId) {
                        return false;
                    }
                }

                return true;
            });

            // Group invoices by customer
            const customerGroups = new Map();

            filteredInvoices.forEach(invoice => {
                const customerId = invoice.customerId;
                const customerName = invoice.customer?.customerName ||
                    invoice.customerName ||
                    `Customer #${customerId}`;

                if (!customerGroups.has(customerId)) {
                    customerGroups.set(customerId, {
                        customerId: customerId,
                        customerName: customerName,
                        invoices: [],
                        totalAmount: 0
                    });
                }

                const group = customerGroups.get(customerId);
                const invoiceData = {
                    invoiceNo: invoice.invoiceNo,
                    date: invoice.date,
                    dueDate: invoice.dueDate,
                    totalAmount: parseFloat(invoice.totalAmount || 0),
                    status: invoice.status,
                    type: 'Invoice'
                };

                group.invoices.push(invoiceData);
                group.totalAmount += invoiceData.totalAmount;
            });

            // Fetch all receive payments for accrual basis
            let payments = [];
            try {
                let paymentsUrl = `/customer/receive-payments?companyId=${companyId}`;
                const paymentsResponse = await this.apiService.get(paymentsUrl);
                
                if (paymentsResponse.data) {
                    if (Array.isArray(paymentsResponse.data)) {
                        payments = paymentsResponse.data;
                    } else if (paymentsResponse.data.data && Array.isArray(paymentsResponse.data.data)) {
                        payments = paymentsResponse.data.data;
                    } else if (paymentsResponse.data.rows && Array.isArray(paymentsResponse.data.rows)) {
                        payments = paymentsResponse.data.rows;
                    }
                }

                // Filter payments by date range if provided
                if (params.fromDate || params.toDate) {
                    payments = payments.filter(payment => {
                        const paymentDate = payment.paymentDate ? (typeof payment.paymentDate === 'string' ? payment.paymentDate.split('T')[0] : payment.paymentDate) : payment.createdAt?.split('T')[0];
                        
                        if (!paymentDate) return false;

                        if (params.fromDate && paymentDate < params.fromDate) {
                            return false;
                        }
                        if (params.toDate && paymentDate > params.toDate) {
                            return false;
                        }
                        return true;
                    });
                }

                // Filter by customer if specified
                if (params.customerId) {
                    payments = payments.filter(payment => {
                        const paymentCustomerId = typeof payment.receivedFrom === 'string' 
                            ? parseInt(payment.receivedFrom) 
                            : payment.receivedFrom;
                        const filterCustomerId = typeof params.customerId === 'string' 
                            ? parseInt(params.customerId) 
                            : params.customerId;
                        return paymentCustomerId === filterCustomerId;
                    });
                }
            } catch (error) {
                logger.warn('Error fetching receive payments for accrual basis:', error);
                // Continue without payments if there's an error
            }

            // Add receive payments to customer groups
            payments.forEach(payment => {
                const customerId = payment.receivedFrom || payment.customerId;
                const customerName = payment.customer?.customerName ||
                    payment.customerName ||
                    `Customer #${customerId}`;

                if (!customerGroups.has(customerId)) {
                    customerGroups.set(customerId, {
                        customerId: customerId,
                        customerName: customerName,
                        invoices: [],
                        totalAmount: 0
                    });
                }

                const group = customerGroups.get(customerId);
                const paymentDate = payment.paymentDate ? (typeof payment.paymentDate === 'string' ? payment.paymentDate.split('T')[0] : payment.paymentDate) : payment.createdAt?.split('T')[0];
                const amount = parseFloat(payment.amountReceived || payment.amount || 0);

                const receiptData = {
                    invoiceNo: payment.reference || `RCPT-${payment.id}`,
                    date: paymentDate,
                    dueDate: paymentDate,
                    totalAmount: -amount, // Negative for accrual basis (payment reduces accounts receivable)
                    status: 'paid',
                    type: 'Receipt'
                };

                group.invoices.push(receiptData);
                group.totalAmount += receiptData.totalAmount;
            });

            // Convert map to array and sort by customer name
            const detailData = Array.from(customerGroups.values())
                .sort((a, b) => a.customerName.localeCompare(b.customerName));

            // Sort invoices within each customer group by date
            detailData.forEach(group => {
                group.invoices.sort((a, b) => new Date(b.date) - new Date(a.date));
            });

            return {
                success: true,
                data: detailData,
                metadata: {
                    totalCustomers: detailData.length,
                    totalInvoices: filteredInvoices.length,
                    totalSales: detailData.reduce((sum, group) => sum + group.totalAmount, 0),
                    fromDate: params.fromDate,
                    toDate: params.toDate,
                    reportBasis: reportBasis
                }
            };
        } catch (error) {
            logger.error('Error fetching sales by customer detail:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Error fetching sales by customer detail',
                error: error
            };
        }
    }

    /**
     * Get Sales by Customer Detail for Cash Basis
     * Returns receive payment records grouped by customer
     */
    async getSalesByCustomerDetailCash(companyId, params = {}) {
        try {
            // Build query parameters for receive payments
            let url = `/customer/receive-payments?companyId=${companyId}`;
            if (params.customerId) {
                url += `&customerId=${params.customerId}`;
            }

            const response = await this.apiService.get(url);

            // Extract payments from response
            let payments = [];
            if (response.data) {
                if (Array.isArray(response.data)) {
                    payments = response.data;
                } else if (response.data.data && Array.isArray(response.data.data)) {
                    payments = response.data.data;
                } else if (response.data.rows && Array.isArray(response.data.rows)) {
                    payments = response.data.rows;
                }
            }

            // Filter payments by date range if provided
            if (params.fromDate || params.toDate) {
                payments = payments.filter(payment => {
                    if (!payment.paymentDate) return false;
                    const paymentDate = payment.paymentDate.split('T')[0]; // Handle both date and datetime

                    if (params.fromDate && paymentDate < params.fromDate) {
                        return false;
                    }
                    if (params.toDate && paymentDate > params.toDate) {
                        return false;
                    }
                    return true;
                });
            }

            // Filter by customer if specified
            if (params.customerId) {
                payments = payments.filter(payment => {
                    const paymentCustomerId = typeof payment.receivedFrom === 'string' 
                        ? parseInt(payment.receivedFrom) 
                        : payment.receivedFrom;
                    const filterCustomerId = typeof params.customerId === 'string' 
                        ? parseInt(params.customerId) 
                        : params.customerId;
                    return paymentCustomerId === filterCustomerId;
                });
            }

            // Group payments by customer
            const customerGroups = new Map();

            payments.forEach(payment => {
                const customerId = payment.receivedFrom || payment.customerId;
                const customerName = payment.customer?.customerName ||
                    payment.customerName ||
                    `Customer #${customerId}`;

                if (!customerGroups.has(customerId)) {
                    customerGroups.set(customerId, {
                        customerId: customerId,
                        customerName: customerName,
                        invoices: [],
                        totalAmount: 0
                    });
                }

                const group = customerGroups.get(customerId);
                const receiptData = {
                    invoiceNo: payment.reference || `RCPT-${payment.id}`,
                    date: payment.paymentDate,
                    dueDate: payment.paymentDate,
                    totalAmount: parseFloat(payment.amountReceived || 0),
                    status: 'paid',
                    type: 'Receipt'
                };

                group.invoices.push(receiptData);
                group.totalAmount += receiptData.totalAmount;
            });

            // Convert map to array and sort by customer name
            const detailData = Array.from(customerGroups.values())
                .sort((a, b) => a.customerName.localeCompare(b.customerName));

            // Sort receipts within each customer group by date
            detailData.forEach(group => {
                group.invoices.sort((a, b) => new Date(b.date) - new Date(a.date));
            });

            return {
                success: true,
                data: detailData,
                metadata: {
                    totalCustomers: detailData.length,
                    totalInvoices: payments.length,
                    totalSales: detailData.reduce((sum, group) => sum + group.totalAmount, 0),
                    fromDate: params.fromDate,
                    toDate: params.toDate,
                    reportBasis: 'cash'
                }
            };
        } catch (error) {
            logger.error('Error fetching sales by customer detail (cash basis):', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Error fetching receive payment records',
                error: error
            };
        }
    }

    /**
     * Export Sales by Customer Summary to CSV
     */
    async exportSalesByCustomerSummaryToCSV(companyId, params = {}) {
        try {
            const result = await this.getSalesByCustomerSummary(companyId, params);

            if (!result.success || !result.data) {
                return {
                    success: false,
                    message: result.message || 'No data available for export'
                };
            }

            // Build CSV content
            let csvContent = 'Customer Name,Amount\n';

            result.data.forEach(item => {
                const customerName = `"${item.customerName.replace(/"/g, '""')}"`;
                const amount = item.amount.toFixed(2);
                csvContent += `${customerName},${amount}\n`;
            });

            // Add total row
            const total = result.data.reduce((sum, item) => sum + item.amount, 0);
            csvContent += `\nTotal,${total.toFixed(2)}\n`;

            // Create and download the CSV file
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;

            // Generate filename with date range
            const dateStr = params.fromDate && params.toDate
                ? `${params.fromDate}_to_${params.toDate}`
                : new Date().toISOString().split('T')[0];
            link.download = `sales_by_customer_summary_${dateStr}.csv`;

            // Trigger download
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up
            window.URL.revokeObjectURL(downloadUrl);

            return {
                success: true,
                message: 'Sales by customer summary exported successfully'
            };
        } catch (error) {
            logger.error('Error exporting sales by customer summary:', error);
            return {
                success: false,
                message: error.message || 'Error exporting sales by customer summary'
            };
        }
    }

    /**
     * Get Sales by Item Summary
     * Aggregates invoice item totals by item name for a given date range
     */
    async getSalesByItemSummary(companyId, params = {}) {
        try {
            const invoices = await this.fetchCompanyInvoicesWithItems(companyId);

            // Filter invoices by date range if provided
            let filteredInvoices = invoices;
            if (params.fromDate || params.toDate) {
                filteredInvoices = filteredInvoices.filter(invoice => {
                    if (!invoice.date) return false;
                    const invoiceDate = invoice.date.split('T')[0];

                    if (params.fromDate && invoiceDate < params.fromDate) {
                        return false;
                    }
                    if (params.toDate && invoiceDate > params.toDate) {
                        return false;
                    }
                    return true;
                });
            }

            // Filter invoices based on report basis and status
            const reportBasis = params.reportBasis || 'accrual';
            filteredInvoices = filteredInvoices.filter(invoice => {
                if (invoice.status === 'draft' || invoice.status === 'cancelled') {
                    return false;
                }

                // Filter by customer if specified
                if (params.customerId) {
                    const invoiceCustomerId = typeof invoice.customerId === 'string' 
                        ? parseInt(invoice.customerId) 
                        : invoice.customerId;
                    const filterCustomerId = typeof params.customerId === 'string' 
                        ? parseInt(params.customerId) 
                        : params.customerId;
                    if (invoiceCustomerId !== filterCustomerId) {
                        return false;
                    }
                }

                if (reportBasis === 'cash') {
                    return invoice.status === 'paid' || invoice.status === 'partially_paid';
                }

                return true;
            });

            // Aggregate by item name
            const itemSalesMap = new Map();

            filteredInvoices.forEach(invoice => {
                if (invoice.items && Array.isArray(invoice.items)) {
                    invoice.items.forEach(item => {
                        const itemName = item.name || 'Unnamed Item';
                        const itemType = item.itemType || 'inventory';
                        const quantity = parseFloat(item.quantity || 0);
                        const hours = parseFloat(item.hours || 0);
                        const hourlyRate = parseFloat(item.hourlyRate || 0);
                        const totalAmount = parseFloat(item.totalAmount || 0);

                        if (itemSalesMap.has(itemName)) {
                            const existing = itemSalesMap.get(itemName);
                            if (itemType === 'consultant') {
                                existing.hours = (existing.hours || 0) + hours;
                                existing.hourlyRate = hourlyRate; // Use the latest hourly rate
                            } else {
                                existing.quantity = (existing.quantity || 0) + quantity;
                            }
                            existing.amount += totalAmount;
                        } else {
                            itemSalesMap.set(itemName, {
                                item: itemName,
                                itemType: itemType,
                                quantity: itemType === 'consultant' ? 0 : quantity,
                                hours: itemType === 'consultant' ? hours : 0,
                                hourlyRate: itemType === 'consultant' ? hourlyRate : 0,
                                amount: totalAmount
                            });
                        }
                    });
                }
            });

            // Convert map to array and sort by item name
            const summaryData = Array.from(itemSalesMap.values())
                .sort((a, b) => a.item.localeCompare(b.item));

            return {
                success: true,
                data: summaryData,
                metadata: {
                    totalItems: summaryData.length,
                    totalSales: summaryData.reduce((sum, item) => sum + item.amount, 0),
                    fromDate: params.fromDate,
                    toDate: params.toDate,
                    reportBasis: reportBasis
                }
            };
        } catch (error) {
            logger.error('Error fetching sales by item summary:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Error fetching sales by item summary',
                error: error
            };
        }
    }

    /**
     * Get Sales by Item Detail
     * Returns detailed invoice item list grouped by item
     * For cash basis, returns receive payment records instead of invoices
     */
    async getSalesByItemDetail(companyId, params = {}) {
        try {
            const reportBasis = params.reportBasis || 'accrual';

            // For cash basis, fetch receive payments instead of invoices
            if (reportBasis === 'cash') {
                return await this.getSalesByItemDetailCash(companyId, params);
            }

            // For accrual basis, use existing invoice logic
            const invoices = await this.fetchCompanyInvoicesWithItems(companyId);

            // Filter invoices by date range if provided
            let filteredInvoices = invoices;
            if (params.fromDate || params.toDate) {
                filteredInvoices = filteredInvoices.filter(invoice => {
                    if (!invoice.date) return false;
                    const invoiceDate = invoice.date.split('T')[0];

                    if (params.fromDate && invoiceDate < params.fromDate) {
                        return false;
                    }
                    if (params.toDate && invoiceDate > params.toDate) {
                        return false;
                    }
                    return true;
                });
            }

            // Filter invoices based on report basis and status
            filteredInvoices = filteredInvoices.filter(invoice => {
                if (invoice.status === 'draft' || invoice.status === 'cancelled') {
                    return false;
                }

                // Filter by customer if specified
                if (params.customerId) {
                    const invoiceCustomerId = typeof invoice.customerId === 'string' 
                        ? parseInt(invoice.customerId) 
                        : invoice.customerId;
                    const filterCustomerId = typeof params.customerId === 'string' 
                        ? parseInt(params.customerId) 
                        : params.customerId;
                    if (invoiceCustomerId !== filterCustomerId) {
                        return false;
                    }
                }

                return true;
            });

            // Group invoice items by item name
            const itemGroups = new Map();

            filteredInvoices.forEach(invoice => {
                if (invoice.items && Array.isArray(invoice.items)) {
                    invoice.items.forEach(item => {
                        const itemName = item.name || 'Unnamed Item';
                        const quantity = parseFloat(item.quantity || 0);
                        const unitPrice = parseFloat(item.unitPrice || 0);
                        const totalAmount = parseFloat(item.totalAmount || 0);

                        if (!itemGroups.has(itemName)) {
                            const itemType = item.itemType || 'inventory';
                            itemGroups.set(itemName, {
                                item: itemName,
                                itemType: itemType,
                                transactions: [],
                                totalQuantity: itemType === 'consultant' ? 0 : 0,
                                totalHours: itemType === 'consultant' ? 0 : 0,
                                hourlyRate: itemType === 'consultant' ? 0 : 0,
                                totalAmount: 0
                            });
                        }

                        const group = itemGroups.get(itemName);
                        const itemType = item.itemType || 'inventory';
                        const hours = parseFloat(item.hours || 0);
                        const hourlyRate = parseFloat(item.hourlyRate || 0);

                        const transactionData = {
                            date: invoice.date,
                            type: 'Invoice',
                            no: invoice.invoiceNo,
                            itemType: itemType,
                            quantity: itemType === 'consultant' ? 0 : quantity,
                            unitPrice: itemType === 'consultant' ? 0 : unitPrice,
                            hours: itemType === 'consultant' ? hours : 0,
                            hourlyRate: itemType === 'consultant' ? hourlyRate : 0,
                            amount: totalAmount
                        };

                        group.transactions.push(transactionData);
                        if (itemType === 'consultant') {
                            group.totalHours = (group.totalHours || 0) + hours;
                            group.hourlyRate = hourlyRate; // Use the latest hourly rate
                        } else {
                            group.totalQuantity = (group.totalQuantity || 0) + quantity;
                        }
                        group.totalAmount += totalAmount;
                        group.itemType = itemType; // Store itemType for the group
                    });
                }
            });

            // Fetch and process receive payments for accrual basis
            try {
                let paymentsUrl = `/customer/receive-payments?companyId=${companyId}`;
                const paymentsResponse = await this.apiService.get(paymentsUrl);
                
                let payments = [];
                if (paymentsResponse.data) {
                    if (Array.isArray(paymentsResponse.data)) {
                        payments = paymentsResponse.data;
                    } else if (paymentsResponse.data.data && Array.isArray(paymentsResponse.data.data)) {
                        payments = paymentsResponse.data.data;
                    } else if (paymentsResponse.data.rows && Array.isArray(paymentsResponse.data.rows)) {
                        payments = paymentsResponse.data.rows;
                    }
                }

                // Filter payments by date range if provided
                if (params.fromDate || params.toDate) {
                    payments = payments.filter(payment => {
                        const paymentDate = payment.paymentDate ? (typeof payment.paymentDate === 'string' ? payment.paymentDate.split('T')[0] : payment.paymentDate) : payment.createdAt?.split('T')[0];
                        
                        if (!paymentDate) return false;

                        if (params.fromDate && paymentDate < params.fromDate) {
                            return false;
                        }
                        if (params.toDate && paymentDate > params.toDate) {
                            return false;
                        }
                        return true;
                    });
                }

                // Filter by customer if specified
                if (params.customerId) {
                    payments = payments.filter(payment => {
                        const paymentCustomerId = typeof payment.receivedFrom === 'string' 
                            ? parseInt(payment.receivedFrom) 
                            : payment.receivedFrom;
                        const filterCustomerId = typeof params.customerId === 'string' 
                            ? parseInt(params.customerId) 
                            : params.customerId;
                        return paymentCustomerId === filterCustomerId;
                    });
                }

                // For each payment, fetch the associated invoice items from the paid invoices
                for (const payment of payments) {
                    if (payment.records && Array.isArray(payment.records)) {
                        for (const record of payment.records) {
                            const invoiceId = record.invoiceId;
                            if (!invoiceId) continue;

                            try {
                                // Fetch invoice items for this invoice
                                const itemsResponse = await this.apiService.get(`/customer/invoice-items?invoiceId=${invoiceId}`);
                                let invoiceItems = [];
                                
                                if (itemsResponse.data) {
                                    if (itemsResponse.data.data && Array.isArray(itemsResponse.data.data)) {
                                        invoiceItems = itemsResponse.data.data;
                                    } else if (itemsResponse.data.items && Array.isArray(itemsResponse.data.items)) {
                                        invoiceItems = itemsResponse.data.items;
                                    } else if (Array.isArray(itemsResponse.data)) {
                                        invoiceItems = itemsResponse.data;
                                    }
                                }

                                // Process each invoice item
                                invoiceItems.forEach(item => {
                                    const itemName = item.name || 'Unnamed Item';
                                    const quantity = parseFloat(item.quantity || 0);
                                    const unitPrice = parseFloat(item.unitPrice || 0);
                                    const totalAmount = parseFloat(item.totalAmount || 0);
                                    const itemType = item.itemType || 'inventory';
                                    const hours = parseFloat(item.hours || 0);
                                    const hourlyRate = parseFloat(item.hourlyRate || 0);

                                    if (!itemGroups.has(itemName)) {
                                        itemGroups.set(itemName, {
                                            item: itemName,
                                            itemType: itemType,
                                            transactions: [],
                                            totalQuantity: itemType === 'consultant' ? 0 : 0,
                                            totalHours: itemType === 'consultant' ? 0 : 0,
                                            hourlyRate: itemType === 'consultant' ? 0 : 0,
                                            totalAmount: 0
                                        });
                                    }

                                    const group = itemGroups.get(itemName);
                                    const paymentDate = payment.paymentDate ? (typeof payment.paymentDate === 'string' ? payment.paymentDate.split('T')[0] : payment.paymentDate) : payment.createdAt?.split('T')[0];

                                    const transactionData = {
                                        date: paymentDate,
                                        type: 'Receipt',
                                        no: payment.reference || `RCPT-${payment.id}`,
                                        itemType: itemType,
                                        quantity: itemType === 'consultant' ? 0 : quantity,
                                        unitPrice: itemType === 'consultant' ? 0 : unitPrice,
                                        hours: itemType === 'consultant' ? hours : 0,
                                        hourlyRate: itemType === 'consultant' ? hourlyRate : 0,
                                        amount: -totalAmount // Negative for accrual basis (payment reduces accounts receivable)
                                    };

                                    group.transactions.push(transactionData);
                                    if (itemType === 'consultant') {
                                        group.totalHours = (group.totalHours || 0) + hours;
                                        group.hourlyRate = hourlyRate; // Use the latest hourly rate
                                    } else {
                                        group.totalQuantity = (group.totalQuantity || 0) + quantity;
                                    }
                                    group.totalAmount += transactionData.amount; // Negative amount
                                    group.itemType = itemType; // Store itemType for the group
                                });
                            } catch (error) {
                                logger.warn(`Error fetching invoice items for invoice ${invoiceId} in payment ${payment.id}:`, error);
                                // Continue with other invoices
                            }
                        }
                    }
                }
            } catch (error) {
                logger.warn('Error fetching receive payments for accrual basis:', error);
                // Continue without payments if there's an error
            }

            // Convert map to array and sort by item name
            const detailData = Array.from(itemGroups.values())
                .sort((a, b) => a.item.localeCompare(b.item));

            // Sort transactions within each item group by date
            detailData.forEach(group => {
                group.transactions.sort((a, b) => new Date(b.date) - new Date(a.date));
            });

            return {
                success: true,
                data: detailData,
                metadata: {
                    totalItems: detailData.length,
                    totalTransactions: detailData.reduce((sum, group) => sum + group.transactions.length, 0),
                    totalSales: detailData.reduce((sum, group) => sum + group.totalAmount, 0),
                    fromDate: params.fromDate,
                    toDate: params.toDate,
                    reportBasis: reportBasis
                }
            };
        } catch (error) {
            logger.error('Error fetching sales by item detail:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Error fetching sales by item detail',
                error: error
            };
        }
    }

    /**
     * Get Sales by Item Detail for Cash Basis
     * Returns receive payment records grouped by item
     */
    async getSalesByItemDetailCash(companyId, params = {}) {
        try {
            // Build query parameters for receive payments
            let url = `/customer/receive-payments?companyId=${companyId}`;
            if (params.customerId) {
                url += `&customerId=${params.customerId}`;
            }

            const response = await this.apiService.get(url);

            // Extract payments from response
            let payments = [];
            if (response.data) {
                if (Array.isArray(response.data)) {
                    payments = response.data;
                } else if (response.data.data && Array.isArray(response.data.data)) {
                    payments = response.data.data;
                } else if (response.data.rows && Array.isArray(response.data.rows)) {
                    payments = response.data.rows;
                }
            }

            // Filter payments by date range if provided
            if (params.fromDate || params.toDate) {
                payments = payments.filter(payment => {
                    if (!payment.paymentDate) return false;
                    const paymentDate = payment.paymentDate.split('T')[0];

                    if (params.fromDate && paymentDate < params.fromDate) {
                        return false;
                    }
                    if (params.toDate && paymentDate > params.toDate) {
                        return false;
                    }
                    return true;
                });
            }

            // Filter by customer if specified
            if (params.customerId) {
                payments = payments.filter(payment => {
                    const paymentCustomerId = typeof payment.receivedFrom === 'string' 
                        ? parseInt(payment.receivedFrom) 
                        : payment.receivedFrom;
                    const filterCustomerId = typeof params.customerId === 'string' 
                        ? parseInt(params.customerId) 
                        : params.customerId;
                    return paymentCustomerId === filterCustomerId;
                });
            }

            // Group invoice items by item name from receive payments
            const itemGroups = new Map();

            // Fetch invoice items for each invoice in payment records
            for (const payment of payments) {
                if (payment.records && Array.isArray(payment.records)) {
                    for (const record of payment.records) {
                        const invoiceId = record.invoiceId;
                        if (!invoiceId) continue;

                        try {
                            // Fetch invoice items for this invoice
                            const itemsResponse = await this.apiService.get(`/customer/invoice-items?invoiceId=${invoiceId}`);
                            let invoiceItems = [];
                            
                            if (itemsResponse.data) {
                                if (itemsResponse.data.data && Array.isArray(itemsResponse.data.data)) {
                                    invoiceItems = itemsResponse.data.data;
                                } else if (itemsResponse.data.items && Array.isArray(itemsResponse.data.items)) {
                                    invoiceItems = itemsResponse.data.items;
                                } else if (Array.isArray(itemsResponse.data)) {
                                    invoiceItems = itemsResponse.data;
                                }
                            }

                            // Process each invoice item
                            invoiceItems.forEach(item => {
                                const itemName = item.name || 'Unnamed Item';
                                const quantity = parseFloat(item.quantity || 0);
                                const unitPrice = parseFloat(item.unitPrice || 0);
                                const totalAmount = parseFloat(item.totalAmount || 0);

                                if (!itemGroups.has(itemName)) {
                                    const itemType = item.itemType || 'inventory';
                                    itemGroups.set(itemName, {
                                        item: itemName,
                                        itemType: itemType,
                                        transactions: [],
                                        totalQuantity: itemType === 'consultant' ? 0 : 0,
                                        totalHours: itemType === 'consultant' ? 0 : 0,
                                        hourlyRate: itemType === 'consultant' ? 0 : 0,
                                        totalAmount: 0
                                    });
                                }

                                const group = itemGroups.get(itemName);
                                const itemType = item.itemType || 'inventory';
                                const hours = parseFloat(item.hours || 0);
                                const hourlyRate = parseFloat(item.hourlyRate || 0);

                                const transactionData = {
                                    date: payment.paymentDate, // Use payment date for cash basis
                                    type: 'Receipt',
                                    no: payment.reference || `RCPT-${payment.id}`,
                                    itemType: itemType,
                                    quantity: itemType === 'consultant' ? 0 : quantity,
                                    unitPrice: itemType === 'consultant' ? 0 : unitPrice,
                                    hours: itemType === 'consultant' ? hours : 0,
                                    hourlyRate: itemType === 'consultant' ? hourlyRate : 0,
                                    amount: totalAmount
                                };

                                group.transactions.push(transactionData);
                                if (itemType === 'consultant') {
                                    group.totalHours = (group.totalHours || 0) + hours;
                                    group.hourlyRate = hourlyRate; // Use the latest hourly rate
                                } else {
                                    group.totalQuantity = (group.totalQuantity || 0) + quantity;
                                }
                                group.totalAmount += totalAmount;
                                group.itemType = itemType; // Store itemType for the group
                            });
                        } catch (error) {
                            logger.error(`Error fetching invoice items for invoice ${invoiceId}:`, error);
                            // Continue processing other invoices
                        }
                    }
                }
            }

            // Convert map to array and sort by item name
            const detailData = Array.from(itemGroups.values())
                .sort((a, b) => a.item.localeCompare(b.item));

            // Sort transactions within each item group by date
            detailData.forEach(group => {
                group.transactions.sort((a, b) => new Date(b.date) - new Date(a.date));
            });

            return {
                success: true,
                data: detailData,
                metadata: {
                    totalItems: detailData.length,
                    totalTransactions: detailData.reduce((sum, group) => sum + group.transactions.length, 0),
                    totalSales: detailData.reduce((sum, group) => sum + group.totalAmount, 0),
                    fromDate: params.fromDate,
                    toDate: params.toDate,
                    reportBasis: 'cash'
                }
            };
        } catch (error) {
            logger.error('Error fetching sales by item detail (cash basis):', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Error fetching receive payment records',
                error: error
            };
        }
    }
}

export default new SalesReportService();

